// components/SelfPaced/AddLessonModal.js
// Theme-aware lesson creation modal — matches REPLIT design
import React, { useState, useRef } from 'react';
import { Modal, Form, Spinner, Row, Col } from 'react-bootstrap';
import {
  X, Clock, Upload, AlertCircle, FileVideo,
} from 'lucide-react';
import { AppButton } from '../ui';
import { LESSON_TYPE_CONFIG } from './LessonItem';
import QuizBuilder from './QuizBuilder';
import {
  createLesson,
  updateLesson,
  confirmLessonUpload,
} from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

const FILE_ACCEPT = {
  video: 'video/mp4,video/webm,video/quicktime,video/*',
  pdf: 'application/pdf',
  ppt: 'application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation',
  docx: 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const needsFile = (t) => ['video', 'pdf', 'ppt', 'docx'].includes(t);

const AddLessonModal = ({
  show,
  onHide,
  initial,
  moduleId,
  courseId,
  onSaved,
  setToast,
}) => {
  const isEdit = !!initial?._id;

  const [form, setForm] = useState({
    title: initial?.title || '',
    type: initial?.type || 'video',
    duration: initial?.duration || '',
    content: initial?.content || '',
    isPreview: initial?.isPreview || false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const quizBuilderRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileDrop = (file) => {
    if (!file) return;
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    // ── Quiz-type lesson: create quiz first, then lesson with quizId ──
    if (form.type === 'quiz' && !isEdit) {
      if (!quizBuilderRef.current) return;
      setSaving(true);
      try {
        // Create the quiz via QuizBuilder's exposed submit
        const quizData = await quizBuilderRef.current.submit();
        if (!quizData) {
          // validation failed inside QuizBuilder — it already showed a toast
          setSaving(false);
          return;
        }

        // Now create the lesson linked to the quiz
        const payload = {
          moduleId,
          courseId,
          title: form.title.trim(),
          type: 'quiz',
          content: '',
          isPreview: form.isPreview,
          quizId: quizData._id,
          fileName: '',
          fileType: '',
        };

        await createLesson(payload);
        setToast({ show: true, message: 'Quiz lesson created!', type: 'success' });
        onSaved();
        onHide();
      } catch (err) {
        setToast({
          show: true,
          message: err?.response?.data?.message || err?.message || 'Failed to create quiz lesson.',
          type: 'error',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!isEdit && needsFile(form.type) && !selectedFile) {
      setToast({
        show: true,
        message: 'Please select a file to upload.',
        type: 'error',
      });
      return;
    }

    setSaving(true);
    setUploadProgress(0);

    try {
      if (isEdit) {
        const payload = {
          title: form.title.trim(),
          content: form.content,
          isPreview: form.isPreview,
          duration: form.duration ? Number(form.duration) : undefined,
        };
        await updateLesson(initial._id, payload);
        setToast({ show: true, message: 'Lesson updated.', type: 'success' });
      } else {
        setUploadPhase('creating');
        const formData = new FormData();
        formData.append('moduleId', moduleId);
        formData.append('courseId', courseId);
        formData.append('title', form.title.trim());
        formData.append('type', form.type);
        formData.append('content', form.content || '');
        formData.append('isPreview', form.isPreview);
        if (selectedFile) {
          formData.append('file', selectedFile);
          formData.append('fileName', selectedFile.name);
          formData.append('fileType', selectedFile.type);
        }

        setUploadPhase('uploading');
        const { data: createRes } = await createLesson(formData);

        setToast({
          show: true,
          message: 'Lesson created & file uploaded!',
          type: 'success',
        });
      }
      onSaved();
      onHide();
    } catch (err) {
      setToast({
        show: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to save lesson.',
        type: 'error',
      });
    } finally {
      setSaving(false);
      setUploadPhase('');
      setUploadProgress(0);
    }
  };

  const typeConfig =
    LESSON_TYPE_CONFIG[form.type] || LESSON_TYPE_CONFIG.text;
  const TypeIcon = typeConfig.icon;

  const uploadZoneClasses = [
    'sp-upload-zone',
    dragActive ? 'sp-upload-zone--drag-active' : '',
    selectedFile ? 'sp-upload-zone--has-file' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      centered
      size="lg"
      backdrop={saving ? 'static' : true}
      className="sp-lesson-modal"
    >
      <Form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="sp-modal__header">
          <div className="sp-modal__header-info">
            <div
              className="sp-modal__icon"
              style={{ background: typeConfig.bg, color: typeConfig.color }}
            >
              <TypeIcon size={18} />
            </div>
            <div>
              <h6 className="sp-modal__title">
                {isEdit ? 'Edit Lesson' : 'Add New Lesson'}
              </h6>
              <p className="sp-modal__subtitle">
                {isEdit
                  ? 'Update lesson details'
                  : 'Create a new lesson for this module'}
              </p>
            </div>
          </div>
          {!saving && (
            <button
              type="button"
              className="sp-modal__close"
              onClick={onHide}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="sp-modal__body">
          {/* Lesson Type Selector */}
          {!isEdit && (
            <div className="sp-modal__field">
              <label className="sp-field-label">Lesson Type</label>
              <div className="sp-lesson-types">
                {Object.entries(LESSON_TYPE_CONFIG).map(([key, cfg]) => {
                  const BtnIcon = cfg.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`sp-lesson-type-btn ${
                        form.type === key ? 'sp-lesson-type-btn--active' : ''
                      }`}
                      onClick={() => setForm((p) => ({ ...p, type: key }))}
                    >
                      <BtnIcon size={20} />
                      <span>{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Title */}
          <div className="sp-modal__field">
            <label className="sp-field-label">
              Lesson Title <span className="text-danger">*</span>
            </label>
            <Form.Control
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="e.g., Introduction to React Hooks"
              disabled={saving}
            />
          </div>

          {/* Duration + Preview */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <label className="sp-field-label">Duration (minutes)</label>
              <div className="sp-input-icon-wrap">
                <Clock size={15} className="sp-input-icon" />
                <Form.Control
                  type="number"
                  min={0}
                  step={0.1}
                  className="sp-input-with-icon"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  placeholder="Auto-detected from video"
                  disabled={saving}
                />
              </div>
            </Col>
            <Col md={6}>
              <label className="sp-field-label">&nbsp;</label>
              <div
                className="sp-preview-toggle"
                onClick={() =>
                  setForm((p) => ({ ...p, isPreview: !p.isPreview }))
                }
              >
                <div
                  className={`sp-toggle-track ${
                    form.isPreview ? 'sp-toggle-track--on' : ''
                  }`}
                >
                  <div className="sp-toggle-thumb" />
                </div>
                <div>
                  <span className="sp-toggle-text">Free Preview</span>
                  <br />
                  <span className="sp-toggle-hint">
                    Available without enrollment
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          {/* File Upload Zone */}
          {!isEdit && needsFile(form.type) && (
            <div className="sp-modal__field">
              <label className="sp-field-label">
                Upload File <span className="text-danger">*</span>
              </label>
              <div
                className={uploadZoneClasses}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  handleFileDrop(e.dataTransfer.files[0]);
                }}
                onClick={() =>
                  !saving &&
                  document.getElementById('spLessonFileInput')?.click()
                }
              >
                <input
                  id="spLessonFileInput"
                  type="file"
                  hidden
                  accept={FILE_ACCEPT[form.type] || '*/*'}
                  onChange={(e) => handleFileDrop(e.target.files?.[0])}
                  disabled={saving}
                />

                {selectedFile ? (
                  <div className="sp-upload-file-info">
                    <div
                      className="sp-upload-file-icon"
                      style={{
                        color: typeConfig.color,
                        background: typeConfig.bg,
                      }}
                    >
                      <FileVideo size={24} />
                    </div>
                    <div className="flex-grow-1">
                      <div className="sp-upload-file-name">
                        {selectedFile.name}
                      </div>
                      <div className="sp-upload-file-size">
                        {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                      </div>
                    </div>
                    {!saving && (
                      <button
                        type="button"
                        className="sp-file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="sp-upload-placeholder">
                    <Upload size={28} strokeWidth={1.5} />
                    <div>
                      <span className="sp-upload-placeholder__text">
                        Drop your {typeConfig.label.toLowerCase()} here
                      </span>
                      <br />
                      <span className="sp-upload-placeholder__hint">
                        or click to browse files
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {saving && uploadPhase === 'uploading' && (
            <div className="sp-upload-progress">
              <div className="sp-upload-progress-header">
                <Upload size={14} />
                <span>Uploading to cloud storage...</span>
                <span className="ms-auto fw-bold">{uploadProgress}%</span>
              </div>
              <div className="sp-upload-progress-track">
                <div
                  className="sp-upload-progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Phase Indicators */}
          {saving && uploadPhase && uploadPhase !== 'uploading' && (
            <div className="sp-phase-indicator">
              <Spinner size="sm" animation="border" />
              <span>
                {uploadPhase === 'creating'
                  ? 'Creating lesson...'
                  : 'Confirming upload...'}
              </span>
            </div>
          )}

          {/* Text Content Editor */}
          {form.type === 'text' && (
            <div className="sp-modal__field">
              <label className="sp-field-label">Lesson Content</label>
              <Form.Control
                as="textarea"
                rows={6}
                name="content"
                value={form.content}
                onChange={handleChange}
                placeholder="Write your lesson content here."
                disabled={saving}
              />
            </div>
          )}

          {/* Quiz Builder (inline, compact mode) */}
          {form.type === 'quiz' && !isEdit && (
            <div className="sp-modal__field">
              <label className="sp-field-label">Quiz Questions</label>
              <QuizBuilder
                ref={quizBuilderRef}
                courseId={courseId}
                moduleId={moduleId}
                setToast={setToast}
                compact
              />
            </div>
          )}

          {/* Re-upload hint for edit */}
          {isEdit && needsFile(initial?.type) && (
            <div className="sp-info-banner">
              <AlertCircle size={15} />
              <span>
                To replace the uploaded file, delete this lesson and create a
                new one.
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sp-modal__footer">
          <AppButton variant="ghost" onClick={onHide} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton type="submit" disabled={saving}>
            {saving
              ? uploadPhase === 'uploading'
                ? `Uploading ${uploadProgress}%`
                : 'Processing...'
              : isEdit
              ? 'Update Lesson'
              : 'Create Lesson'}
          </AppButton>
        </div>
      </Form>
    </Modal>
  );
};

export default AddLessonModal;
